/** @jsxImportSource @emotion/react */

import { notification } from "antd";
import { MatchApi } from "api/Match";
import { MatchItemType } from "api/Match/type";
import {
  BG_MatchSchudle,
  BG_Stadium_Grass,
  Button_Bet_Now,
  ICON_VS_MATCH,
} from "assets/Common/images/matchSchudle";
import { ResponseCode } from "contants/response";
import { handleLoginGame } from "helpers/handleLoginGame";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import { i18n } from "i18n";
import { useEffect, useState } from "react";
import Slider, { Settings } from "react-slick";
import { selectUsername } from "store/auth";
import { StatusNotification } from "types";

import ErrorMatch from "./ErrorMatch";
import { MatchScheduleStyle } from "./index.style";
import LoadingMatch from "./LoadingMatch";

interface MatchScheduleProps {
  onActionClick?: (match: MatchItemType) => void;
}

type MatchScheduleStatus = "loading" | "success" | "error";

const DEFAULT_ACTION_LABEL = "Bet Now";
const DEFAULT_ERROR_MESSAGE = "Unable to load match data";
const DEFAULT_EMPTY_MESSAGE = "No match data available";

const getTeamInitials = (teamName: string) =>
  (teamName || "TBD")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const formatTimezone = (timezone?: string) => {
  const normalizedTimezone = timezone?.trim().replace(/^\((.*)\)$/, "$1");

  if (!normalizedTimezone) {
    return "";
  }

  return `(${normalizedTimezone})`;
};

const getSlideKey = (match: MatchItemType, index: number) =>
  [
    match.leagueName,
    match.homeTeamName,
    match.awayTeamName,
    match.matchDate,
    match.matchTime,
    index,
  ]
    .filter(Boolean)
    .join("-");

const EmptyState = () => (
  <div className="match-card match-state-card">
    <div className="state-message state-message-empty">
      {DEFAULT_EMPTY_MESSAGE}
    </div>
  </div>
);

const MatchSchedule = ({ onActionClick }: MatchScheduleProps) => {
  const [matches, setMatches] = useState<MatchItemType[]>([]);
  const [status, setStatus] = useState<MatchScheduleStatus>("loading");
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const [failedLogoMap, setFailedLogoMap] = useState<Record<string, boolean>>(
    {}
  );

  const [api, contextHolder] = notification.useNotification();
  const openNotification = (status: StatusNotification, message: string) => {
    api[status]({
      message,
      description: "",
      key: "LoginGame",
      duration: 20,
    });
  };

  const dispatch = useAppDispatch();
  const username = useAppSelector(selectUsername);

  useEffect(() => {
    let isMounted = true;

    const getMatchData = async () => {
      setStatus("loading");
      setErrorMessage(DEFAULT_ERROR_MESSAGE);
      setFailedLogoMap({});

      try {
        const response = await MatchApi.getMatch();

        if (!isMounted) {
          return;
        }

        if (response.status === ResponseCode.SUCCESS) {
          setMatches(response.data || []);
          setStatus("success");
          return;
        }

        setMatches([]);
        setStatus("error");
        setErrorMessage(response.msg || DEFAULT_ERROR_MESSAGE);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setMatches([]);
        setStatus("error");
        setErrorMessage(DEFAULT_ERROR_MESSAGE);
      }
    };

    getMatchData();

    return () => {
      isMounted = false;
    };
  }, [i18n.language]);

  const handleLogoError = (logoKey: string) => {
    setFailedLogoMap((prev) => {
      if (prev[logoKey]) {
        return prev;
      }

      return {
        ...prev,
        [logoKey]: true,
      };
    });
  };

  const renderTeamLogo = (
    logo: string | undefined,
    teamName: string,
    logoKey: string
  ) => {
    if (logo && !failedLogoMap[logoKey]) {
      return (
        <img
          className="team-logo"
          src={logo}
          alt={teamName}
          onError={() => handleLogoError(logoKey)}
        />
      );
    }

    return (
      <div className="team-logo-fallback">{getTeamInitials(teamName)}</div>
    );
  };

  const settings: Settings = {
    arrows: false,
    infinite: matches.length > 1,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: matches.length > 1,
    autoplaySpeed: 10000,
    speed: 500,
    adaptiveHeight: false,
  };

  return (
    <section css={MatchScheduleStyle.wrapper} aria-busy={status === "loading"}>
      <div className="match-schedule-frame">
        <div
          className="match-schedule-bg"
          style={{ backgroundImage: `url(${BG_MatchSchudle})` }}
        />
        <div
          className="match-stadium-layer"
          aria-hidden="true"
          style={{ backgroundImage: `url(${BG_Stadium_Grass})` }}
        />

        {status === "loading" && <LoadingMatch />}

        {status === "error" && (
          <ErrorMatch message={errorMessage || DEFAULT_ERROR_MESSAGE} />
        )}

        {status === "success" && !matches.length && <EmptyState />}

        {status === "success" && !!matches.length && (
          <Slider {...settings} className="match-schedule-slider">
            {matches.map((match, index) => {
              const slideKey = getSlideKey(match, index);
              const homeTeamName = match.homeTeamName || "Home Team";
              const awayTeamName = match.awayTeamName || "Away Team";
              const timezone = formatTimezone(match.timezone);
              const homeLogoKey = `${slideKey}-home`;
              const awayLogoKey = `${slideKey}-away`;

              return (
                <div key={slideKey}>
                  <div className="match-card">
                    <div className="league-badge">
                      {match.leagueName || "Match Schedule"}
                    </div>

                    <div className="match-time-row">
                      <span>{match.matchDate || "--"}</span>
                      <span className="time-dot">&bull;</span>
                      <span className="match-time">
                        {match.matchTime || "--"}
                      </span>
                      {timezone && (
                        <span className="match-timezone">{timezone}</span>
                      )}
                    </div>

                    <div className="match-hero">
                      <div className="team-block">
                        <div className="team-odds">
                          {match.homeOdds || "--"}
                        </div>
                        <div className="team-logo-shell">
                          {renderTeamLogo(
                            match.homeTeamLogo,
                            homeTeamName,
                            homeLogoKey
                          )}
                        </div>
                        <div className="team-caption team-caption-home">
                          <p className="team-name">{homeTeamName}</p>
                          <span className="team-side-label">Home</span>
                        </div>
                      </div>

                      <div className="versus">
                        <img src={ICON_VS_MATCH} alt="Versus" />
                      </div>

                      <div className="team-block">
                        <div className="team-odds">
                          {match.awayOdds || "--"}
                        </div>
                        <div className="team-logo-shell">
                          {renderTeamLogo(
                            match.awayTeamLogo,
                            awayTeamName,
                            awayLogoKey
                          )}
                        </div>
                        <div className="team-caption team-caption-away">
                          <p className="team-name">{awayTeamName}</p>
                          <span className="team-side-label">Away</span>
                        </div>
                      </div>
                    </div>

                    <div className="match-footer">
                      <button
                        type="button"
                        className="action-button"
                        style={{ backgroundImage: `url(${Button_Bet_Now})` }}
                        onClick={() => {
                          handleLoginGame(
                            {
                              game_code: "",
                              name: "",
                              provider_code: "BF",
                              type: "SB",
                              featured: "",
                              new_arrive: "",
                              picture: "",
                            },
                            username,
                            dispatch,
                            openNotification
                          );
                        }}
                      >
                        {DEFAULT_ACTION_LABEL}
                      </button>
                    </div>

                    <div className="market-summary">
                      <span className="market-label">HDP</span>
                      <span className="market-value">
                        {match.handicap || "--"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        )}
      </div>
      {contextHolder}
    </section>
  );
};

export default MatchSchedule;
